import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { components } from 'react-select';
import styled from 'styled-components';
import useTranslation from '../../../../util/hooks/useTranslation';
import Tree from './Tree';
import { TreeSelectContext } from './index';
import cx from 'classnames';

export const Dropdown = ({ children, isOpen, target, onClose }) => (
    <div className={'select-dropdown'}>
        {target}
        {isOpen ? <Menu>{children}</Menu> : null}
        {isOpen ? <Blanket onClick={onClose} /> : null}
    </div>
);

export const Menu = props => {
    return <div className={'select-menu'} {...props} />;
};

export const Blanket = props => <div className={'select-blanket'} {...props} />;

export const ValueContainer = ({ selected, onClick, isOpen, totalLength }) => {
    return (
        <StyledContainer onClick={onClick} isOpen={isOpen}>
            <div className={cx('select-container', isOpen && 'container-open')}>
                <div>
                    <ValueContainerText selected={selected} totalLength={totalLength} />
                </div>
                <div>
                    <span className="material-icons-round">arrow_drop_down</span>
                </div>
            </div>
        </StyledContainer>
    );
};

// 하위 노드가 모두 선택 되어있을 때, 부모노드를 선택항목으로 추가
export function mergeSelected(flattenData, treeKey, parentKey, selected, selectedNode) {
    let mergedSelected = [...selected];

    // 부모 노드키
    const parents = selectedNode
        ? [selectedNode[parentKey]]
        : selected.reduce((acc, curr) => {
              if (curr[parentKey]) {
                  acc.push(curr[parentKey]);
              }
              return acc;
          }, []);

    // 자식 노드가 모두 선택되어 있는지 체크
    const childrenLenCheck = parents.reduce((acc, curr) => {
        const childrenLen = flattenData.filter(v => v[parentKey] === curr).length;
        const selectedLen = selected.filter(v => v[parentKey] === curr).length;
        if (curr) {
            acc[curr] = childrenLen === selectedLen;
        }
        return acc;
    }, {});

    // 상위 자식이 선택되어 있는 경우 하위 자식 요소 제거
    for (let parent in childrenLenCheck) {
        if (childrenLenCheck.hasOwnProperty(parent) && childrenLenCheck[parent]) {
            const parentNode = flattenData.find(node => node[treeKey] === parent);
            const childrenIdList = findAllChildren(flattenData, treeKey, parentKey, [parentNode])
                .map(node => node[treeKey])
                .filter(nodeId => nodeId !== parent);
            childrenIdList.forEach(childrenId => {
                if (childrenLenCheck[childrenId]) {
                    childrenLenCheck[childrenId] = null;
                    delete childrenLenCheck[childrenId];
                }
            });
        }
    }

    // 자식이 모두 선택되어 있다면 부모도 선택 항목에 추가
    for (let parent in childrenLenCheck) {
        if (childrenLenCheck.hasOwnProperty(parent)) {
            const checked = childrenLenCheck[parent];
            if (checked) {
                const foundIndex = mergedSelected.findIndex(v => v[parentKey] === parent);
                mergedSelected = removeAllChildren(mergedSelected, treeKey, parentKey, parent);
                if (!mergedSelected.find(v => v[treeKey] === parent)) {
                    const parentNode = flattenData.find(v => v[treeKey] === parent);
                    if (parentNode) {
                        mergedSelected.splice(foundIndex, 0, parentNode);
                        mergedSelected = mergeSelected(flattenData, treeKey, parentKey, mergedSelected, parentNode);
                    }
                }
            }
        }
    }

    return mergedSelected;
}

/**
 * 모든 자식 요소를 제거한 목록 반환
 *
 * @param nodeList 대상 목록
 * @param treeKey 키 필드명
 * @param parentKey 부모 키 필드명
 * @param parentId 찾을 부모 ID
 *
 * @return parentId의 모든 자식 요소가 제거된 목록
 *
 * */
const removeAllChildren = (nodeList, treeKey, parentKey, parentId) => {
    const childrenList = [];
    nodeList.forEach(node => {
        if (node[parentKey] === parentId) {
            const nodeId = node[treeKey];
            childrenList.push(nodeId);
        }
    });
    let removedList = nodeList.filter(node => !childrenList.includes(node[treeKey]));
    childrenList.forEach(newParentId => {
        if (removedList.find(node => node[parentKey] === newParentId)) {
            removedList = removeAllChildren(removedList, treeKey, parentKey, newParentId);
        }
    });

    return removedList;
}

/**
 * 선택한 목록의 목록의 모든 자식 요소가 포함된 목록 반환
 *
 * @param flattenData 전체 목록
 * @param treeKey 키 필드명
 * @param parentKey 부모 키 필드명
 * @param selected 선택한 요소의 목록
 *
 * @return selected의 모든 자식 요소가 포함된 목록
 *
 * */
export const findAllChildren = (flattenData, treeKey, parentKey, selected) => {
    let allNode = [...selected];
    selected.forEach(selectedNode => {
        if (selectedNode) {
            const findId = selectedNode[treeKey];
            flattenData.forEach(node => {
                if (node[parentKey] === findId && !allNode.find(v => v[treeKey] === node[treeKey])) {
                    allNode.push(...findAllChildren(flattenData, treeKey, parentKey, [node]));
                }
            });
        }
    });
    return allNode;
};

export const ValueContainerText = ({ selected, totalLength }) => {
    const t = useTranslation('TreeSelect');
    const { title, labelKey, treeKey, parentKey, flattenData } = useContext(TreeSelectContext);
    const mergedSelection = useMemo(() => {
        return mergeSelected(flattenData, treeKey, parentKey, selected);
    }, [treeKey, parentKey, flattenData, selected]);
    const selectedLength = mergedSelection ? mergedSelection.length : 0;
    const totalSelectedLength = mergedSelection ? mergedSelection.filter(v => !v[parentKey]).length : 0;
    return !selectedLength || totalSelectedLength === totalLength ? (
        <strong>
            {title} : {t('All')}
        </strong>
    ) : selectedLength === 1 ? (
        <strong>{mergedSelection[0][labelKey]}</strong>
    ) : (
        <>
            <strong>{mergedSelection[0][labelKey]}</strong>
            <span>
                &nbsp;{t('other')} {selectedLength - 1}
            </span>
        </>
    );
};

export const Svg = p => <svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" {...p} />;
export const DropdownIndicator = () => (
    <div className={'select-dropdown-indicator'}>
        <Svg>
            <path
                d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
                fill="currentColor"
                fillRule="evenodd"
            />
        </Svg>
    </div>
);

export const findAllOptions = (options, allOptions = []) => {
    options.forEach(option => {
        allOptions.push(option.value);
        if (option.children) {
            findAllOptions(option.children, allOptions);
        }
    });
    return allOptions;
};

export const TreeMenuList = ({ children, getValue, setValue, options, ...restProps }) => {
    const t = useTranslation('TreeSelect');
    const selected = getValue();
    const { valueKey, labelKey, treeKey, parentKey, flattenData } = useContext(TreeSelectContext);
    const mergedSelection = useMemo(() => {
        return mergeSelected(flattenData, treeKey, parentKey, selected);
    }, [treeKey, parentKey, flattenData, selected]);

    const allOptions = useMemo(() => {
        return findAllOptions(options);
    }, [options]);

    const handleChange = e => {
        const { checked } = e.currentTarget;
        setValue(checked ? allOptions : []);
    };

    // // 자기 자신과 하위 노드들을 선택
    // // @param checked 체크된 노드들의 valueKey
    // // @return 하위 노드들을 포함한 노드
    // const makeCheckedDescendantsNode = useCallback(
    //     checked => {
    //         let descendantsNode = [];
    //         checked.forEach(v => {
    //             descendantsNode.push(flattenData.find(original => original[valueKey] === v));
    //             descendantsNode = descendantsNode.concat(
    //                 flattenData.filter(original => ~original[valueKey].indexOf(v + '>')),
    //             );
    //         });
    //         return descendantsNode.map(v => v[valueKey]);
    //     },
    //     [valueKey, flattenData],
    // );
    //
    // // 초기 체크될 값
    // const [checked, setChecked] = useState(selected ? makeCheckedDescendantsNode(selected.map(v => v[valueKey])) : []);
    const [checked, setChecked] = useState(
        selected.map(v => {
            if (typeof v === 'object') {
                return v[valueKey];
            }
            return v;
        }),
    );

    const handleCheck = useCallback(checked => {
        setChecked(checked);
    }, []);

    useEffect(() => {
        setChecked(
            selected.map(v => {
                if (typeof v === 'object') {
                    return v[valueKey];
                }
                return v;
            }),
        );
    }, [selected.length]);

    // useEffect(() => {
    //     const calculatedChecked = [];
    //     checked.forEach(treePath => {
    //         const lastNode = calculatedChecked[calculatedChecked.length - 1];
    //         if (!lastNode || (lastNode && treePath.indexOf(lastNode[valueKey] + '>') !== 0)) {
    //             calculatedChecked.push(flattenData.find(option => option[valueKey] === treePath));
    //         }
    //     });
    //     setValue(calculatedChecked);
    // }, [checked]);

    useEffect(() => {
        const checkedToStr = checked.map(v => v.toString());
        setValue(flattenData.filter(v => checkedToStr.includes(v[valueKey].toString())).map(v => v[valueKey]));
    }, [checked, flattenData, valueKey]);

    return (
        <components.MenuList {...restProps}>
            <div className={'mb-2'}>
                <div className={'select-group-title'}>{t('Selected')}</div>
                {mergedSelection.length ? (
                    mergedSelection.map((v, index) => {
                        return (
                            <div
                                className={'styled-option d-flex'}
                                key={v[valueKey] + index}
                                onClick={() => {
                                    const filtered = mergedSelection.filter(
                                        selectedOption => selectedOption[valueKey] !== v[valueKey],
                                    );
                                    // setValue(filtered);
                                    setChecked(
                                        findAllChildren(flattenData, treeKey, parentKey, filtered).map(
                                            selectedOption => selectedOption[valueKey],
                                        ),
                                    );
                                }}
                            >
                                <label className="pnt-checkbox h-auto" htmlFor={v[labelKey]}>
                                    <input
                                        id={v[labelKey]}
                                        type="checkbox"
                                        style={{ cursor: 'pointer' }}
                                        value={v[valueKey]}
                                        checked
                                        onChange={() => null}
                                    />
                                    <span className="material-icons-round md-18 color-secondary">check_box</span>
                                    <p>{v[labelKey]}</p>
                                </label>
                            </div>
                        );
                    })
                ) : (
                    <div className={'styled-option-label'}>{t('Not Selected')}</div>
                )}
            </div>
            <div className={'select-group-title pt-0 pb-0'}>
                <label className="pnt-checkbox h-auto align-center" htmlFor={'All_Items'}>
                    <input
                        id={'All_Items'}
                        type="checkbox"
                        onChange={handleChange}
                        checked={selected.length === allOptions.length}
                    />
                    <span
                        className={`material-icons-round md-18 ${
                            selected.length === allOptions.length ? 'color-secondary' : ''
                        }`}
                    >
                        {selected.length === allOptions.length ? 'check_box' : 'check_box_outline_blank'}
                    </span>
                    <p>{t('All Items')}</p>
                </label>
            </div>
            {!selected.length && !options.length ? (
                <div className={'styled-option-label'}>{t('No matches found')}</div>
            ) : (
                <div style={{ padding: '.5rem' }}>
                    <Tree data={options} checked={checked} setChecked={handleCheck} />
                    {/*{children}*/}
                </div>
            )}
        </components.MenuList>
    );
};

const findParentNodeSubRows = (path, searchList) => {
    if (path.length > 1) {
        const currPath = path.shift();
        return findParentNodeSubRows(path, searchList.find(node => node.floorId === currPath).children);
    } else {
        return searchList;
    }
};

export const StyledContainer = styled.div`
    position: relative;
    box-sizing: border-box;
    min-width: 8rem;
    //max-width: 14rem;
    width: 100%;
    & > div:hover {
        border-color: hsl(0, 0%, 70%);
    }
    & > div {
        align-items: center;
        background-color: hsl(0, 0%, 100%);
        border: ${props => (props.isOpen ? '1px solid #2684ff' : '1px solid hsl(0, 0%, 80%)')};
        box-shadow: ${props => props.isOpen && '0 0 0 1px #2684ff'};
        border-radius: 2px;
        cursor: pointer;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        min-height: 28px;
        outline: 0 !important;
        position: relative;
        transition: all 100ms;
        box-sizing: border-box;

        div:first-child {
            align-items: center;
            display: flex;
            flex: 1;
            flex-wrap: wrap;
            padding: 2px 8px;
            position: relative;
            overflow: hidden;
            box-sizing: border-box;
        }
        div:last-child {
            align-items: center;
            align-self: stretch;
            display: flex;
            flex-shrink: 0;
            padding: 6px;
            transition: color 150ms;
            span {
                font-size: 1rem;
                box-sizing: border-box;
                align-self: stretch;
            }
            div {
                color: hsl(0, 0%, 80%);
                display: flex;
                box-sizing: border-box;
                span {
                    display: inline-block;
                    fill: currentColor;
                    line-height: 1;
                    stroke: currentColor;
                    stroke-width: 0;
                }
            }
            div:hover {
                color: hsl(0, 0%, 60%);
            }
        }
    }
`;
